import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@eo/shared';

import type { HoursStepperProps } from './types';

const STEP = 0.25;

// The browser's own spinner sits on top of the digits and ignores the theme, so
// the arrows live inside the field's border instead, stepping by the quarter
// hour ClickUp keeps.
export function HoursStepper({
  id,
  label,
  value,
  min,
  disabled = false,
  onCommit,
  className,
}: HoursStepperProps) {
  // Only what is being typed lives here, so "1." on the way to "1.5" is never
  // read as a value and the field otherwise shows what the bill holds.
  const [draft, setDraft] = useState<string | null>(null);

  const settle = (next: number) => {
    const clamped = Math.max(min, Math.round(next * 4) / 4);
    setDraft(null);
    if (clamped !== value) onCommit(clamped);
  };
  const commitTyped = () => {
    const typed = Number(draft);
    if (draft === null || draft.trim() === '' || !Number.isFinite(typed) || typed < min) {
      setDraft(null);
    } else settle(typed);
  };
  const step = (direction: 1 | -1) => settle(value + direction * STEP);

  return (
    <div
      className={cn(
        'flex h-8 items-stretch overflow-hidden rounded-md border border-control-border bg-control transition-colors focus-within:border-focus',
        disabled && 'bg-control-disabled',
        className,
      )}
    >
      <input
        id={id}
        role="spinbutton"
        inputMode="decimal"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={min}
        value={draft ?? String(value)}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitTyped}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          step(event.key === 'ArrowUp' ? 1 : -1);
        }}
        className="min-w-0 flex-1 bg-transparent px-2 text-right text-sm tabular-nums text-control-foreground focus:outline-none disabled:cursor-not-allowed disabled:text-control-disabled-foreground"
      />
      <div className="flex w-6 flex-col border-l border-control-border">
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Increase ${label}`}
          disabled={disabled}
          onClick={() => step(1)}
          className="flex flex-1 items-center justify-center text-text-tertiary transition-colors hover:text-text-primary disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronUp className="size-3" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Decrease ${label}`}
          disabled={disabled || value <= min}
          onClick={() => step(-1)}
          className="flex flex-1 items-center justify-center border-t border-control-border text-text-tertiary transition-colors hover:text-text-primary disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronDown className="size-3" />
        </button>
      </div>
    </div>
  );
}
